import RankingStat from "../../entities/RankingStat.js";
import NumberFormatter from "../../formatters/NumberFormatter.js";
import DerivativeRankingStatCalculatorBase from "./DerivativeRankingStatCalculatorBase.js";

class RatioRankingStatCalculator extends DerivativeRankingStatCalculatorBase {
    constructor(numberFormatter) {
        if (typeof(numberFormatter) !== 'undefined' && !(numberFormatter instanceof NumberFormatter)) {
            throw new TypeError('numberFormatter must be an instance inheriting from NumberFormatter class');
        }

        super();
        this.parameters = [];

        this.numberFormatter = numberFormatter || new NumberFormatter(3);
    }

    registerParameter(parameter, dividend, divider) {
        if (!this.isParameterValid(dividend)) {
            throw new TypeError('dividend must be an object containing key and aggregator properties');
        }
        if (!this.isParameterValid(divider)) {
            throw new TypeError('divider must be an object containing key and aggregator properties');
        }
        if (this.canCalculate(parameter)) {
            throw new Error(`Parameter ${parameter.key} is already registered`);
        }

        this.parameters.push({
            parameter, dividend, divider
        });
    }

    listDependencies(parameter) {
        if (!this.canCalculate(parameter)) {
            throw new Error(`Parameter ${parameter.key} is not supported`);
        }

        const parameterRatio = this.parameters.find(rp => rp.parameter === parameter);
        return [parameterRatio.dividend, parameterRatio.divider];
    }

    canCalculateStat(parameter) {
        return this.parameters.some(rp => rp.parameter === parameter);
    }

    calculateStat(parameter, dependencies) {
        const paramterRatio = this.parameters.find(rp => rp.parameter === parameter);
        const dividend = dependencies.find(p => p.parameter === paramterRatio.dividend.key);
        if (!dividend) {
            throw new Error(`Unable to calculate ${parameter.key} - dividend ${paramterRatio.dividend.key} is not available in passed dependencies`);
        }
        if (!(dividend instanceof RankingStat)) {
            throw new Error(`Unable to calculate ${parameter.key} - dividend ${paramterRatio.dividend.key} is not an instance inheriting from RankingStat class`);
        }

        const divider = dependencies.find(p => p.parameter === paramterRatio.divider.key);
        if (!divider) {
            throw new Error(`Unable to calculate ${parameter.key} - divider ${paramterRatio.divider.key} is not available in passed dependencies`);
        }
        if (!(divider instanceof RankingStat)) {
            throw new Error(`Unable to calculate ${parameter.key} - divider ${paramterRatio.divider.key} is not an instance inheriting from RankingStat class`);
        }
        
        if (divider.value === 0 || divider.isEmpty()) {
            throw new Error(`Unable to calculate ${parameter.key} - divider ${paramterRatio.divider.key} value found in dependencies is equal to zero`);
        }

        // TODO - maybe some enum here?
        if (parameter.type && parameter.type === 'PERCENT') {
            return RankingStat.fromParameter(
                parameter, this.numberFormatter.format((dividend.value * 100) / divider.value), divider.value
            );
        }

        return RankingStat.fromParameter(
            parameter, this.numberFormatter.format(dividend.value / divider.value), divider.value
        );
    }
}

export default RatioRankingStatCalculator;